import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminPackInputSchema, formatAmount, type AdminPack, type AdminPackInput } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Badge, BottomSheet, Button, Card, Spinner, Toast } from '../../components/ui';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { adminErrorText, creditsCount, parseSettingValue } from '../../lib/format';
import { starsUsdHint } from '../../lib/purchase';
import { haptic } from '../../lib/tma';

const inputClass =
  'w-full rounded-lg px-4 py-2.5 text-sm bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70';

type PriceField = 'priceKzt' | 'priceRub' | 'priceUsd' | 'priceStars';
const PRICE_FIELDS: Array<{ field: PriceField; currency: 'KZT' | 'RUB' | 'USD' | 'XTR' }> = [
  { field: 'priceKzt', currency: 'KZT' },
  { field: 'priceRub', currency: 'RUB' },
  { field: 'priceUsd', currency: 'USD' },
  { field: 'priceStars', currency: 'XTR' },
];

interface Form {
  title: string;
  credits: string;
  priceKzt: string;
  priceRub: string;
  priceUsd: string;
  priceStars: string;
  sort: string;
  adminsOnly: boolean;
  active: boolean;
}

const EMPTY: Form = {
  title: '',
  credits: '',
  priceKzt: '0',
  priceRub: '0',
  priceUsd: '0',
  priceStars: '0',
  sort: '0',
  adminsOnly: false,
  active: true,
};

const formOf = (p: AdminPack): Form => ({
  title: p.title,
  credits: String(p.credits),
  priceKzt: String(p.priceKzt),
  priceRub: String(p.priceRub),
  priceUsd: String(p.priceUsd),
  priceStars: String(p.priceStars),
  sort: String(p.sort),
  adminsOnly: p.visibility === 'admins',
  active: p.active,
});

/** The typed form as the API takes it, or null while something is off. */
function inputOf(f: Form): AdminPackInput | null {
  const num = (s: string) => parseSettingValue(s);
  const parsed = adminPackInputSchema.safeParse({
    title: f.title,
    credits: num(f.credits),
    priceKzt: num(f.priceKzt),
    priceRub: num(f.priceRub),
    priceUsd: num(f.priceUsd),
    priceStars: num(f.priceStars),
    visibility: f.adminsOnly ? 'admins' : 'all',
    sort: num(f.sort),
    active: f.active,
  });
  return parsed.success ? parsed.data : null;
}

/** «Пакеты» (spec 4.1): what the Mini App sells. Prices are typed here, never in code. */
export function AdminPacksPage() {
  const t = useT();
  const a = t.adminPay;
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ id: number | null; form: Form } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const packs = useQuery({ queryKey: ['admin', 'packs'], queryFn: () => api.adminListPacks() });

  const flash = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3500);
  };

  const save = useMutation({
    mutationFn: ({ id, body }: { id: number | null; body: AdminPackInput }) =>
      id === null ? api.adminCreatePack(body) : api.adminUpdatePack(id, body),
    onSuccess: () => {
      haptic('success');
      setEditing(null);
      flash(a.packSaved);
      void qc.invalidateQueries({ queryKey: ['payment-options'] });
    },
    onError: (err) => flash(adminErrorText(err, t)),
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'packs'] }),
  });

  const form = editing?.form ?? null;
  const input = form ? inputOf(form) : null;
  const set = (patch: Partial<Form>) => setEditing((e) => (e ? { ...e, form: { ...e.form, ...patch } } : e));

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={a.packs} to="/admin" />

      <Button variant="ink" size="sm" className="mb-4" onClick={() => setEditing({ id: null, form: EMPTY })}>
        {a.packAdd}
      </Button>

      {packs.isLoading && <Spinner size="sm" ariaLabel={t.common.loading} />}
      {packs.isError && <p className="text-sm text-hint">{t.common.failed}</p>}
      {packs.data && packs.data.packs.length === 0 && <p className="text-sm text-hint">{a.packsEmpty}</p>}

      <div className="space-y-2">
        {packs.data?.packs.map((p) => (
          <Card key={p.id} variant="surface" padding="md" interactive onClick={() => setEditing({ id: p.id, form: formOf(p) })}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-bold">{p.title}</span>
              <span className="text-sm text-hint tabular-nums">{creditsCount(p.credits)}</span>
            </div>
            <div className="mt-1 text-sm tabular-nums">
              {PRICE_FIELDS.map(({ field, currency }) => (p[field] > 0 ? formatAmount(p[field], currency) : '—')).join(' · ')}
            </div>
            {(p.visibility === 'admins' || !p.active) && (
              <div className="mt-2 flex gap-2">
                {p.visibility === 'admins' && <Badge>{a.packAdminsBadge}</Badge>}
                {!p.active && <Badge>{a.packHiddenBadge}</Badge>}
              </div>
            )}
          </Card>
        ))}
      </div>

      <BottomSheet
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id === null ? a.packNew : a.packEdit}
      >
        {form && (
          <div className="space-y-3 pb-1">
            <label className="block text-xs text-hint">
              {a.packTitle}
              <input value={form.title} maxLength={60} onChange={(e) => set({ title: e.target.value })} className={`${inputClass} mt-1`} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-hint">
                {a.packCredits}
                <input inputMode="numeric" value={form.credits} onChange={(e) => set({ credits: e.target.value })} className={`${inputClass} mt-1`} />
              </label>
              <label className="block text-xs text-hint">
                {a.packSort}
                <input inputMode="numeric" value={form.sort} onChange={(e) => set({ sort: e.target.value })} className={`${inputClass} mt-1`} />
              </label>
              {PRICE_FIELDS.map(({ field }) => (
                <label key={field} className="block text-xs text-hint">
                  {
                    { priceKzt: a.packPriceKzt, priceRub: a.packPriceRub, priceUsd: a.packPriceUsd, priceStars: a.packPriceStars }[
                      field
                    ]
                  }
                  <input inputMode="numeric" value={form[field]} onChange={(e) => set({ [field]: e.target.value })} className={`${inputClass} mt-1`} />
                  {field === 'priceStars' && parseSettingValue(form.priceStars) !== null && (
                    <span className="mt-1 block">{a.packStarsHint(starsUsdHint(parseSettingValue(form.priceStars)!))}</span>
                  )}
                </label>
              ))}
            </div>
            <p className="text-xs text-hint">{a.packZeroHint}</p>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.adminsOnly} onChange={(e) => set({ adminsOnly: e.target.checked })} />
              {a.packAdminsOnly}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
              {a.packActive}
            </label>
            {!input && <p className="text-xs text-destructive">{a.packBad}</p>}
            <Button
              variant="ink"
              fullWidth
              disabled={!input || save.isPending}
              loading={save.isPending}
              onClick={() => input && editing && save.mutate({ id: editing.id, body: input })}
            >
              {a.save}
            </Button>
          </div>
        )}
      </BottomSheet>

      {toast && (
        <Toast tone="neutral" placement="fixed">
          {toast}
        </Toast>
      )}
    </div>
  );
}
